import React, { useEffect, useMemo, useRef, useState } from 'react';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import LocalOpsSheet from './LocalOpsSheet';

const LAYER_META = {
  players: {
    title: 'Players',
    description: "Who's nearby and active",
    Icon: PeopleAltOutlinedIcon,
  },
  posts: {
    title: 'Posts',
    description: 'Local moments & updates',
    Icon: PhotoCameraOutlinedIcon,
  },
  offerings: {
    title: 'Offerings',
    description: 'Products and services you can engage',
    Icon: StorefrontOutlinedIcon,
  },
  tasks: {
    title: 'Tasks',
    description: 'Live requests and jobs',
    Icon: AssignmentOutlinedIcon,
  },
};

export default function LocalOpsLayersSheet({
  open,
  onClose,
  layers,
  onToggle,
  disabled = {},
  counts = {},
  travelMode = false,
  onToggleTravel,
  travelLabel,
  radius,
  unit = 'mi',
  onRadiusChange,
  onUnitChange,
}) {
  const [radiusValue, setRadiusValue] = useState(
    Number.isFinite(radius) ? radius : 25,
  );
  const radiusTimerRef = useRef(null);
  useEffect(() => {
    if (Number.isFinite(radius)) {
      setRadiusValue(radius);
    }
  }, [radius]);
  useEffect(
    () => () => {
      if (radiusTimerRef.current) clearTimeout(radiusTimerRef.current);
    },
    [],
  );

  const entries = useMemo(
    () => ['players', 'posts', 'offerings', 'tasks'],
    [],
  );

  return (
    <LocalOpsSheet open={open} onClose={onClose} title="Layers">
      <div className="localops-section">
        <div className="localops-helper">
          Toggle what is rendered. Layers that are off stay dormant—no network,
          no CPU.
        </div>
      </div>
      {entries.map((key) => {
        const isDisabled = !!disabled[key];
        const meta = LAYER_META[key];
        const Icon = meta.Icon;
        const count = Number.isFinite(counts[key]) ? counts[key] : 0;
        return (
          <div
            className={`localops-layer-card ${
              layers[key] ? 'is-on' : 'is-off'
            } ${isDisabled ? 'is-disabled' : ''}`}
            key={key}
          >
            <div className="localops-layer-icon">
              <Icon fontSize="small" />
            </div>
            <div className="localops-layer-info">
              <div className="localops-layer-title-row">
                <span className="localops-layer-title">{meta.title}</span>
                <span
                  className="localops-count-badge"
                  key={`${key}-${count}`}
                >
                  {count}
                </span>
              </div>
              <div className="localops-layer-desc">{meta.description}</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!layers[key]}
              className={`localops-toggle-switch ${
                layers[key] ? 'is-on' : ''
              }`}
              disabled={isDisabled}
              onClick={() => onToggle?.(key)}
            >
              <span className="localops-toggle-switch__thumb" />
            </button>
          </div>
        );
      })}
      <div className="localops-layer-divider" />
      <div className="localops-layer-card localops-layer-card--travel">
        <div className="localops-layer-icon">
          <ExploreOutlinedIcon fontSize="small" />
        </div>
        <div className="localops-layer-info">
          <div className="localops-layer-title-row">
            <span className="localops-layer-title">Radius</span>
            {radius ? (
              <span className="localops-count-badge">
                {radius} {unit}
              </span>
            ) : null}
          </div>
          <div className="localops-layer-desc">
            Adjust your LocalOps discovery range.
          </div>
          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={radiusValue}
            onChange={(e) => {
              const nextVal = Number(e.target.value);
              setRadiusValue(nextVal);
              if (radiusTimerRef.current)
                clearTimeout(radiusTimerRef.current);
              radiusTimerRef.current = setTimeout(() => {
                onRadiusChange?.(nextVal);
              }, 180);
            }}
            className="localops-radius-slider"
          />
          <div className="localops-unit-toggle">
            <button
              type="button"
              className={`localops-unit-btn ${unit === 'mi' ? 'is-on' : ''}`}
              onClick={() => onUnitChange?.('mi')}
            >
              mi
            </button>
            <button
              type="button"
              className={`localops-unit-btn ${unit === 'km' ? 'is-on' : ''}`}
              onClick={() => onUnitChange?.('km')}
            >
              km
            </button>
          </div>
        </div>
      </div>
      <div className="localops-layer-card localops-layer-card--travel">
        <div className="localops-layer-icon">
          <ExploreOutlinedIcon fontSize="small" />
        </div>
        <div className="localops-layer-info">
          <div className="localops-layer-title-row">
            <span className="localops-layer-title">Travel mode</span>
            {travelLabel ? (
              <span className="localops-count-badge">{travelLabel}</span>
            ) : null}
          </div>
          <div className="localops-layer-desc">
            Planning ahead? Pan to a town and explore what's there before you
            arrive.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!travelMode}
          className={`localops-toggle-switch ${
            travelMode ? 'is-on' : ''
          }`}
          onClick={() => onToggleTravel?.()}
        >
          <span className="localops-toggle-switch__thumb" />
        </button>
      </div>
    </LocalOpsSheet>
  );
}
